/**
 * DEFCAT P5 slice 3 — per-user Defect Type Collections management (Settings).
 *
 * Collections are ordered, per-user subsets of a catalogue's entries (TYPE
 * and/or LOCATION) that narrow the New-Defect drawer's pickers (concept §2c /
 * workflow 2c). This page lets a user, for a chosen catalogue:
 *   • create / rename / delete collections,
 *   • set visibility (private | tenant),
 *   • pick the entries in a collection (type + location leaves),
 *   • reorder them, and
 *   • set the ACTIVE collection (or clear it → full catalogue).
 *
 * It composes the existing collections + two-axis catalogue data layers; no
 * new endpoints. Lives in Lyse (catalogue context) and is mounted by the
 * umbrella shell at /account/defect-collections.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Radio,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PageHeading } from "@aeriosiq/ui";
import type {
  CollectionVisibility,
  DefectCatalogue,
  DefectCatalogueEntryV2,
  DefectCollection,
} from "@aeriosiq/types";
import { useDefectCataloguesQuery } from "./queries.js";
import { useDefectCatalogueV2Query } from "./queriesTwoAxis.js";
import {
  useCreateCollectionMutation,
  useDefectCollectionsQuery,
  useDeleteCollectionMutation,
  useSetActiveCollectionMutation,
  useUpdateCollectionMutation,
} from "./collectionsQueries.js";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  PencilIcon,
  PlusIcon,
  Trash2Icon,
} from "@aeriosiq/ui/icons";

const VISIBILITIES: { value: CollectionVisibility; label: string }[] = [
  { value: "private", label: "Private (only me)" },
  { value: "tenant", label: "Shared in my tenant" },
];

export function DefectCollectionsManager(): React.ReactElement {
  const cataloguesQuery = useDefectCataloguesQuery();
  const catalogues: DefectCatalogue[] = useMemo(
    () => cataloguesQuery.data?.items ?? [],
    [cataloguesQuery.data],
  );
  const [catalogueId, setCatalogueId] = useState<string | null>(null);
  // Default to the system-default catalogue (or the first) once loaded.
  useEffect(() => {
    if (catalogueId !== null) return;
    if (catalogues.length === 0) return;
    const def = catalogues.find((c) => c.isSystemDefault) ?? catalogues[0];
    setCatalogueId(def?.catalogueId ?? null);
  }, [catalogueId, catalogues]);

  const collectionsQuery = useDefectCollectionsQuery(catalogueId);
  const v2Query = useDefectCatalogueV2Query(catalogueId);
  const collections = useMemo(
    () =>
      [...(collectionsQuery.data?.items ?? [])].sort(
        (a, b) => a.sortOrder - b.sortOrder,
      ),
    [collectionsQuery.data],
  );
  const activeCollectionId = collectionsQuery.data?.activeCollectionId ?? null;

  const createMut = useCreateCollectionMutation();
  const updateMut = useUpdateCollectionMutation(catalogueId ?? "");
  const deleteMut = useDeleteCollectionMutation(catalogueId ?? "");
  const setActiveMut = useSetActiveCollectionMutation();

  // -- Create dialog ------------------------------------------------------
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newVisibility, setNewVisibility] =
    useState<CollectionVisibility>("private");

  const submitCreate = (): void => {
    const name = newName.trim();
    if (!name || !catalogueId) return;
    createMut.mutate(
      { catalogueId, name, visibility: newVisibility },
      {
        onSuccess: () => {
          setCreateOpen(false);
          setNewName("");
          setNewVisibility("private");
        },
      },
    );
  };

  // -- Entry-editor dialog ------------------------------------------------
  const [editing, setEditing] = useState<DefectCollection | null>(null);
  const [selectedEntryIds, setSelectedEntryIds] = useState<Set<string>>(
    new Set(),
  );
  const openEditor = (c: DefectCollection): void => {
    setEditing(c);
    setSelectedEntryIds(new Set(c.entryIds));
  };
  const toggleEntry = (entryId: string): void => {
    setSelectedEntryIds((prev) => {
      const next = new Set(prev);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });
  };
  const submitEntries = (): void => {
    if (!editing) return;
    updateMut.mutate(
      {
        collectionId: editing.collectionId,
        body: { entryIds: [...selectedEntryIds] },
      },
      {
        onSuccess: () => {
          setEditing(null);
        },
      },
    );
  };

  // Entry options for the editor, split by axis (leaves only).
  const { typeLeaves, locationLeaves } = useMemo(() => {
    const entries = v2Query.data?.entries ?? [];
    const isLeaf = (e: DefectCatalogueEntryV2): boolean => e.isLeaf === true;
    return {
      typeLeaves: entries.filter((e) => e.axis === "type" && isLeaf(e)),
      locationLeaves: entries.filter((e) => e.axis === "location" && isLeaf(e)),
    };
  }, [v2Query.data]);

  // -- Rename (inline prompt via the create dialog's machinery) -----------
  const [renaming, setRenaming] = useState<DefectCollection | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const openRename = (c: DefectCollection): void => {
    setRenaming(c);
    setRenameValue(c.name);
  };
  const submitRename = (): void => {
    const name = renameValue.trim();
    if (!renaming || !name) return;
    updateMut.mutate(
      { collectionId: renaming.collectionId, body: { name } },
      {
        onSuccess: () => {
          setRenaming(null);
        },
      },
    );
  };

  const setActive = (collectionId: string | null): void => {
    if (!catalogueId) return;
    setActiveMut.mutate({
      catalogueId,
      ...(collectionId ? { collectionId } : {}),
    });
  };

  const move = (index: number, dir: -1 | 1): void => {
    const a = collections[index];
    const b = collections[index + dir];
    if (!a || !b) return;
    // Swap sort orders (two PATCHes). Static, user-defined ordering.
    updateMut.mutate({
      collectionId: a.collectionId,
      body: { sortOrder: b.sortOrder },
    });
    updateMut.mutate({
      collectionId: b.collectionId,
      body: { sortOrder: a.sortOrder },
    });
  };

  const remove = (c: DefectCollection): void => {
    deleteMut.mutate(c.collectionId);
  };

  return (
    <Box sx={{ pb: 6 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <PageHeading>Defect Type Collections</PageHeading>
        <Button
          variant="contained"
          startIcon={<PlusIcon />}
          onClick={() => {
            setCreateOpen(true);
          }}
          disabled={!catalogueId}
          data-testid="collection-new"
        >
          New collection
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Collections narrow the New-Defect drawer to a chosen subset of a
        catalogue&apos;s defect types and locations. Set one active to use it by
        default; switch back to the full catalogue any time from the drawer.
      </Typography>

      <FormControl size="small" sx={{ minWidth: 280, mb: 2 }}>
        <InputLabel id="collection-catalogue-label">Catalogue</InputLabel>
        <Select
          labelId="collection-catalogue-label"
          label="Catalogue"
          value={catalogueId ?? ""}
          onChange={(e) => {
            setCatalogueId(e.target.value || null);
          }}
          data-testid="collection-catalogue-select"
        >
          {catalogues.map((c) => (
            <MenuItem key={c.catalogueId} value={c.catalogueId}>
              {c.name.en}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {collectionsQuery.isError ? (
        <Alert severity="error">Could not load collections.</Alert>
      ) : null}

      {collectionsQuery.isPending && catalogueId ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : collections.length === 0 ? (
        <Alert severity="info" data-testid="collection-empty">
          No collections yet for this catalogue. Create one to get started.
        </Alert>
      ) : (
        <List data-testid="collection-list">
          {collections.map((c, i) => {
            const isActive = c.collectionId === activeCollectionId;
            return (
              <ListItem
                key={c.collectionId}
                divider
                data-testid={`collection-row-${c.collectionId}`}
                secondaryAction={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Tooltip title="Move up">
                      <span>
                        <IconButton
                          size="small"
                          disabled={i === 0}
                          onClick={() => {
                            move(i, -1);
                          }}
                          aria-label="Move up"
                        >
                          <ArrowUpIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move down">
                      <span>
                        <IconButton
                          size="small"
                          disabled={i === collections.length - 1}
                          onClick={() => {
                            move(i, 1);
                          }}
                          aria-label="Move down"
                        >
                          <ArrowDownIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Edit entries">
                      <IconButton
                        size="small"
                        onClick={() => {
                          openEditor(c);
                        }}
                        aria-label="Edit entries"
                        data-testid={`collection-edit-${c.collectionId}`}
                      >
                        <PencilIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => {
                          remove(c);
                        }}
                        aria-label="Delete"
                        data-testid={`collection-delete-${c.collectionId}`}
                      >
                        <Trash2Icon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                }
              >
                <Radio
                  checked={isActive}
                  onChange={() => {
                    setActive(c.collectionId);
                  }}
                  size="small"
                  inputProps={{ "aria-label": `Set ${c.name} active` }}
                  data-testid={`collection-active-${c.collectionId}`}
                />
                <ListItemButton
                  onClick={() => {
                    openRename(c);
                  }}
                  sx={{ borderRadius: 1 }}
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span>{c.name}</span>
                        {isActive ? (
                          <Chip label="Active" size="small" color="primary" />
                        ) : null}
                        <Chip
                          label={
                            c.visibility === "tenant" ? "Tenant" : "Private"
                          }
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                    }
                    secondary={`${String(c.entryIds.length)} ${
                      c.entryIds.length === 1 ? "entry" : "entries"
                    }`}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      )}

      {collections.length > 0 ? (
        <Button
          size="small"
          onClick={() => {
            setActive(null);
          }}
          disabled={!activeCollectionId}
          sx={{ mt: 1 }}
          data-testid="collection-clear-active"
        >
          Clear active (use full catalogue)
        </Button>
      ) : null}

      {/* Create dialog */}
      <Dialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
        }}
      >
        <DialogTitle>New collection</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1, minWidth: 320 }}>
            <TextField
              label="Name"
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value);
              }}
              size="small"
              data-testid="collection-name-input"
            />
            <FormControl size="small">
              <InputLabel id="new-visibility-label">Visibility</InputLabel>
              <Select
                labelId="new-visibility-label"
                label="Visibility"
                value={newVisibility}
                onChange={(e) => {
                  setNewVisibility(e.target.value as CollectionVisibility);
                }}
              >
                {VISIBILITIES.map((v) => (
                  <MenuItem key={v.value} value={v.value}>
                    {v.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitCreate}
            disabled={newName.trim().length === 0 || createMut.isPending}
            data-testid="collection-create-submit"
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename dialog */}
      <Dialog
        open={renaming != null}
        onClose={() => {
          setRenaming(null);
        }}
      >
        <DialogTitle>Rename collection</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={renameValue}
            onChange={(e) => {
              setRenameValue(e.target.value);
            }}
            size="small"
            sx={{ mt: 1, minWidth: 320 }}
            data-testid="collection-rename-input"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRenaming(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitRename}
            disabled={renameValue.trim().length === 0 || updateMut.isPending}
            data-testid="collection-rename-submit"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Entry editor dialog */}
      <Dialog
        open={editing != null}
        onClose={() => {
          setEditing(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit entries — {editing?.name}</DialogTitle>
        <DialogContent dividers>
          {typeLeaves.length === 0 && locationLeaves.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              This catalogue has no selectable entries yet.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {typeLeaves.length > 0 ? (
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Defect types
                  </Typography>
                  {typeLeaves.map((e) => (
                    <FormControlLabel
                      key={e.entryId}
                      sx={{ display: "flex" }}
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedEntryIds.has(e.entryId)}
                          onChange={() => {
                            toggleEntry(e.entryId);
                          }}
                        />
                      }
                      label={e.name.en}
                    />
                  ))}
                </Box>
              ) : null}
              {typeLeaves.length > 0 && locationLeaves.length > 0 ? (
                <Divider />
              ) : null}
              {locationLeaves.length > 0 ? (
                <Box>
                  <Typography variant="overline" color="text.secondary">
                    Locations
                  </Typography>
                  {locationLeaves.map((e) => (
                    <FormControlLabel
                      key={e.entryId}
                      sx={{ display: "flex" }}
                      control={
                        <Checkbox
                          size="small"
                          checked={selectedEntryIds.has(e.entryId)}
                          onChange={() => {
                            toggleEntry(e.entryId);
                          }}
                        />
                      }
                      label={e.name.en}
                    />
                  ))}
                </Box>
              ) : null}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditing(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitEntries}
            disabled={updateMut.isPending}
            data-testid="collection-entries-submit"
          >
            Save entries
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
